// AddProductOptionsModal.jsx  (FULL DROP-IN WORKING VERSION)

import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { X, Edit2 } from "react-feather";
import Select from "react-select";
import AddProductEditModal from "./AddProductEditModal";
import { SaleService } from "../../../../services/sale/saleService.ts";
import { apiService } from "../../../../services/apiService.ts";

function getPriceDisplay(priceOption) {
  if (!priceOption) return "";
  const v =
    priceOption.displayPrice ??
    priceOption.priceDisplay ??
    priceOption.PriceDisplay ??
    priceOption.unitPrice ??
    priceOption.UnitPrice ??
    priceOption.ItemPrice ??
    priceOption.itemPrice ??
    priceOption.Price ??
    priceOption.price;

  if (v == null) return "";
  if (typeof v === "string") {
    const n = Number(v);
    if (!Number.isNaN(n)) return `$${n.toFixed(2)}`;
    return v;
  }
  if (typeof v === "number") {
    return `$${v.toFixed(2)}`;
  }
  return String(v);
}

const guestOptionsMock = [
  { value: "Carl", label: "Carl" },
  { value: "John", label: "John" },
  { value: "Mary", label: "Mary" },
];

export default function AddProductOptionsModal({
  isOpen,
  onClose,
  productName,
  selectedItems,
  productId,
  editIndex,
  accountId,
}) {
  const [rows, setRows] = useState([]);
  const [editingRow, setEditingRow] = useState(null);
  const [productCombinations, setProductCombinations] = useState([]);
  const [loadingCombinations, setLoadingCombinations] = useState(false);
  const [guestList, setGuestList] = useState([]);

  useEffect(() => {
    if (!isOpen || !selectedItems || !selectedItems.length) return;

    const accountIdSafe = accountId;
    if (!accountIdSafe) {
      setGuestList([]);
      return;
    }

    async function loadGuests() {
      try {
        const res = await apiService.getAccountGuests(accountIdSafe);

        if (res?.Success && Array.isArray(res.Data)) {
          // FORMAT FOR react-select
          const opts = res.Data.map(g => ({
            value: `${g.FirstName} ${g.LastName}`,
            label: `${g.FirstName} ${g.LastName}`,
            guestId: g.GuestID,
            accountGuestId: g.AccountGuestID,
            isResponsible: g.IsResponsible
          }));

          setGuestList(opts);
        } else {
          setGuestList([]);
        }
      } catch (err) {
        console.error("Failed to load guests:", err);
        setGuestList([]);
      }
    }

    loadGuests();
  }, [isOpen, selectedItems, accountId]);


  // Load product combinations for this product
  useEffect(() => {
    if (!isOpen || !productId) {
      setProductCombinations([]);
      return;
    }

    let cancelled = false;

    async function loadCombinations() {
      try {
        setLoadingCombinations(true);
        const combos = await SaleService.getProductCombinations(Number(productId));
        if (!cancelled) {
          setProductCombinations(combos || []);
        }
      } catch (err) {
        console.error("Failed to load product combinations:", err);
        if (!cancelled) {
          setProductCombinations([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingCombinations(false);
        }
      }
    }

    loadCombinations();

    return () => {
      cancelled = true;
    };
  }, [isOpen, productId]);

  // Build rows from selectedItems (1 row per qty)
  useEffect(() => {
    if (!isOpen || !selectedItems || !selectedItems.length) {
      return;
    }

    const temp = [];

    // Use the first selected item as the base for option metadata
    const baseItem = selectedItems[0];
    const basePriceMeta =
      (baseItem && (baseItem.priceOption || baseItem.PriceOptionRaw)) ||
      baseItem ||
      null;

    selectedItems.forEach((item, idx) => {
      const qty = item.qty ?? 1;

      for (let i = 0; i < qty; i++) {
        // 1) Start from raw priceOption (edited or original)
        let priceOption =
          item.priceOption ?? item.PriceOptionRaw ?? item;

        // 2) If this is a FREE substitute (price = 0) and it has no
        //    proper code/description, inherit it from the base product
        if (priceOption && basePriceMeta) {
          const rawPrice =
            priceOption.displayPrice ??
            priceOption.priceDisplay ??
            priceOption.PriceDisplay ??
            priceOption.unitPrice ??
            priceOption.UnitPrice ??
            priceOption.ItemPrice ??
            0;

          if (Number(rawPrice) === 0) {
            priceOption = {
              ...priceOption,
              PriceCode:
                priceOption.PriceCode ??
                basePriceMeta.PriceCode ??
                basePriceMeta.priceCode,
              Description:
                priceOption.Description ??
                basePriceMeta.Description ??
                basePriceMeta.description,
              description:
                priceOption.description ??
                basePriceMeta.description ??
                basePriceMeta.Description,
            };
          }
        }

        // Build from real row data (edit mode) OR fallback to selectedItems (add mode)
        const source = {
          guest: item.guest ?? item.Guest ?? null,
          extraNotes: item.extraNotes ?? item.ExtraNotes ?? "",
          autoNotes: item.autoNotes ?? item.AutoNotes ?? [],
          ingredientNotes: item.ingredientNotes ?? item.IngredientNotes ?? {},
          selectionState: item.selectionState ?? item.SelectionState ?? {},
          combinations:
            item.combinations ??
            item.CombinationSets ??
            productCombinations,
          priceOption,
        };

        temp.push({
          id: `${idx}-${i}-${item.PriceCodeID ?? item.PriceCode ?? "pc"}`,

          // Use the actual product on each row if available (so child shows as Side Salad)
          label:
            item.Product ??
            item.ProductName ??
            item.label ??
            productName,

          // keep child flag if present so other logic (guest dropdown) works
          _isChild: item._isChild ?? false,

          guest: source.guest,
          priceOption: source.priceOption,
          combinations: source.combinations,

          autoNotes: source.autoNotes,
          ingredientNotes: source.ingredientNotes,
          extraNotes: source.extraNotes,
          selectionState: source.selectionState,
          FK_ProductID: productId,
        });

      }
    });

    setRows(temp);

}, [isOpen, selectedItems, productName, productCombinations, productId]);

  if (!isOpen) return null;

  const guestOptions = guestList;

  return (
    <>
      <div
        className="modal fade show d-block"
        style={{
          background: "rgba(0,0,0,0.55)",
          zIndex: 20000,
        }}
      >
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content rounded-4 shadow-lg">

            {/* HEADER */}
            <div className="modal-header border-0 pb-2">
              <h5 className="modal-title w-100 text-center fw-semibold">
                Configure {productName}
              </h5>
              <button
                className="btn btn-light"
                onClick={onClose}
                style={{ borderRadius: "50%" }}
              >
                <X size={18} />
              </button>
            </div>

            {/* BODY */}
            <div className="modal-body">
              {loadingCombinations && (
                <p className="text-muted small mb-3">Loading ingredient options…</p>
              )}

              {rows.map((row, idx) => (
                <div
                  key={row.id}
                  className="p-3 border rounded mb-3"
                  style={{
                    background: "#f8f9fc",
                    borderRadius: "12px",
                    borderColor: "#e3e6ef",
                  }}
                >
                  {/* init collapse state */}
                  {typeof row._expanded === "undefined" && (row._expanded = false)}

                  {/* TOP ROW */}
                  <div
                    className="d-flex justify-content-between align-items-center"
                    style={{ gap: "20px" }}
                    onClick={() => {
                      let updated = [...rows];
                      updated[idx]._expanded = !updated[idx]._expanded;
                      setRows(updated);
                    }}
                  >
                    {/* LEFT: Edit + Product name */}
                    <div
                      className="d-flex align-items-center flex-grow-1"
                      style={{ cursor: "pointer" }}
                    >
                      <button
                        className="btn btn-light border btn-sm me-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingRow({ row, index: idx });
                        }}
                        style={{
                          width: "30px",
                          height: "30px",
                          borderRadius: "50%",
                          padding: 0,
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <Edit2 size={15} />
                      </button>

                      <span className="fw-semibold" style={{ fontSize: "1.1rem" }}>
                        {row.label}
                      </span>
                    </div>

                    {/* GUEST */}
                    <div style={{ minWidth: "230px" }} onClick={(e) => e.stopPropagation()}>
                      <Select
                        isDisabled={row._isChild === true}            // 🔒 child rows cannot be changed
                        options={guestOptions}
                        placeholder="Select guest"
                        value={
                          row.guest
                            ? { value: row.guest, label: row.guest }
                            : null
                        }
                        onChange={(opt) => {
                          // 🔒 If it's a child row — STOP, do nothing
                          if (row._isChild === true) return;

                          let updated = [...rows];

                          // Update parent row
                          updated[idx].guest = opt?.value ?? null;
                          updated[idx].guestId = opt?.guestId ?? null;
                          updated[idx].accountGuestId = opt?.accountGuestId ?? null;

                          // 🔄 Propagate guest to ALL child rows
                          updated = updated.map((r) => {
                            if (r._isChild) {
                              return {
                                ...r,
                                guest: opt?.value ?? null,
                                guestId: opt?.guestId ?? null,
                                accountGuestId: opt?.accountGuestId ?? null,
                              };
                            }
                            return r;
                          });

                          setRows(updated);
                        }}
                      />

                    </div>

                    {/* PRICE */}
                    <div style={{ textAlign: "right", minWidth: "180px", cursor: "pointer" }}>
                      <div className="small text-muted">
                        Option: <strong>
                          {row.priceOption?.PriceCode ??
                            row.priceOption?.Description ??
                            row.priceOption?.description ??
                            "N/A"}
                        </strong>
                      </div>
                      <div
                        style={{
                          color: "#00a36c",
                          fontWeight: 600,
                          fontSize: "1.05rem",
                        }}
                      >
                        {row._isChild && row.priceOption?.IsExtraCharge === false
                          ? ""                              // free → no price
                          : getPriceDisplay(row.priceOption)}
                      </div>
                    </div>
                  </div>

                  {/* COLLAPSIBLE NOTES */}
                  {row._expanded && (
                    <div className="mt-3">
                      <div className="d-flex" style={{ gap: "20px" }}>
                        {/* LEFT: Additional notes */}
                        <div style={{ flex: 1 }}>
                          <label className="small text-muted">Additional Notes:</label>
                          <textarea
                            className="form-control"
                            style={{ minHeight: "120px", resize: "vertical", fontSize: "0.9rem" }}
                            value={row.extraNotes || ""}
                            onChange={(e) => {
                              let updated = [...rows];
                              updated[idx].extraNotes = e.target.value;
                              setRows(updated);
                            }}
                          />
                        </div>

                        {/* RIGHT: Auto Notes */}
                        <div style={{ flex: 1 }}>
                          <label className="small text-muted">Notes:</label>

                          {(row.autoNotes?.length > 0 ||
                            Object.values(row.ingredientNotes || {}).some((v) => v)) ? (
                            <div
                              className="p-3 border rounded"
                              style={{ background: "white", fontSize: "0.9rem" }}
                            >
                              {row.autoNotes?.map((n, i) => (
                                <div key={i}>{n}</div>
                              ))}

                              {Object.entries(row.ingredientNotes || {})
                                .filter(([, v]) => v && v.trim().length > 0)
                                .map(([name, val]) => (
                                  <div key={name}>
                                    {name}: {val}
                                  </div>
                                ))}
                            </div>
                          ) : (
                            <div className="text-muted small">No notes yet</div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* FOOTER */}
            <div className="modal-footer border-0 pt-0">
              <button
                className="btn btn-primary w-100"
                onClick={() => {
                  const timestamp = Date.now();

                  const formatted = rows.map((r, index) => {

                    // 🔥 1) CHILD ROWS — return EXACTLY AS THEY ARE. DO NOT PROCESS FURTHER.
                    if (r._isChild) {
                      const priceOption = r.priceOption || r.PriceOptionRaw || {};

                      const fkPriceCodeId =
                        r.FK_PriceCodeID ??
                        priceOption.priceCodeId ??
                        priceOption.PriceCodeID ??
                        null;

                      const priceCode =
                        r.PriceCode ??
                        priceOption.priceCode ??
                        priceOption.PriceCode ??
                        null;

                      const vat =
                        r.Vat ??
                        priceOption.vat ??
                        priceOption.Vat ??
                        0;

                      const unitPrice = Number(
                        r.UnitPrice ??
                        priceOption.UnitCostIncl?.parsedValue ??
                        priceOption.ItemPrice ??
                        priceOption.UnitPrice ??
                        priceOption.unitPrice ??
                        priceOption.Price ??
                        priceOption.price ??
                        0
                      );

                      const unit =
                        r.Unit ??
                        priceOption.unit ??
                        priceOption.Unit ??
                        null;

                      const priceDesc =
                        r.PriceDescription ??
                        priceOption.Description ??
                        priceOption.description ??
                        "";

                      return {
                        TabLineID: r.TabLineID ?? `TEMP-${timestamp}-${index}`,

                        FK_ProductID: r.FK_ProductID ?? r.productId ?? null,
                        Product: r.Product ?? r.label ?? null,

                        FK_PriceCodeID: fkPriceCodeId,
                        PriceCode: priceCode,
                        UnitPrice: unitPrice,
                        Vat: vat,
                        Unit: unit,
                        PriceDescription: priceDesc,

                        _isChild: true,
                        PointerID: r.PointerID ?? null,
                        SubstituteName: r.SubstituteName ?? null,

                        Quantity: r.Quantity ?? 1,
                        Guest: r.guest ?? null,
                        GuestId: r.guestId ?? null,
                        ExtraNotes: r.ExtraNotes ?? r.extraNotes ?? "",
                        AutoNotes: r.AutoNotes ?? r.autoNotes ?? [],
                        IngredientNotes: r.IngredientNotes ?? r.ingredientNotes ?? {},
                        SelectionState: r.SelectionState ?? r.selectionState ?? {},
                        PriceOptionRaw: r.PriceOptionRaw ?? priceOption,
                        CombinationSets: r.CombinationSets ?? r.combinations ?? [],
                        UnitCostIncl: {
                          parsedValue: unitPrice,
                        },
                      };
                    }

                    // 🔥 2) NORMAL (NON-CHILD) ROWS — processed as before
                    const priceOption = r.priceOption || {};

                    return {
                      TabLineID: `TEMP-${timestamp}-${index}`,

                      FK_ProductID:
                        priceOption.productId ??
                        priceOption.ProductID ??
                        r.productId ??
                        productId,

                      Product:
                        priceOption.Product ??
                        priceOption.ProductName ??
                        r.label,

                      FK_PriceCodeID: priceOption.priceCodeId ?? priceOption.PriceCodeID ?? null,
                      PriceCode: priceOption.priceCode ?? priceOption.PriceCode ?? null,
                      UnitPrice: Number(
                        priceOption.UnitCostIncl?.parsedValue ??
                        priceOption.UnitPrice ??
                        priceOption.unitPrice ??
                        priceOption.Price ??
                        priceOption.price ??
                        0
                      ),
                      Vat: priceOption.vat ?? priceOption.Vat ?? null,
                      Unit: priceOption.unit ?? priceOption.Unit ?? null,
                      PriceDescription: priceOption.description ?? priceOption.Description ?? "",

                      _isChild: false,            // 🔥 NEVER forced from before
                      PointerID: null,           // 🔥 NEVER copied from child
                      SubstituteName: null,

                      Quantity: 1,
                      Guest: r.guest ?? null,
                      GuestId: r.guestId ?? null,
                      ExtraNotes: r.extraNotes ?? "",
                      AutoNotes: r.autoNotes ?? [],
                      IngredientNotes: r.ingredientNotes ?? {},
                      SelectionState: r.selectionState ?? {},
                      PriceOptionRaw: priceOption,
                      CombinationSets: r.combinations ?? [],
                      UnitCostIncl: {
                        parsedValue: Number(
                          priceOption.UnitCostIncl?.parsedValue ??
                          priceOption.UnitPrice ??
                          priceOption.unitPrice ??
                          priceOption.Price ??
                          priceOption.price ??
                          0
                        ),
                      },
                    };
                  });


                  if (editIndex !== null && typeof window.__editTempProduct === "function") {
                    window.__editTempProduct(editIndex, formatted[0]);
                  } else if (typeof window.__addProductsToPosTemp === "function") {
                    window.__addProductsToPosTemp(formatted);
                  }

                  onClose();
                }}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* EDIT MODAL */}
      {editingRow && (
        <AddProductEditModal
          isOpen
          rowData={editingRow.row}
          onClose={() => setEditingRow(null)}
          onSave={(payload) => {
            const results = Array.isArray(payload) ? payload : [payload];

            if (!results.length) {
              setEditingRow(null);
              return;
            }

            let updated = [...rows];

            // MAIN PRODUCT UPDATE
            const main = results[0];

            updated[editingRow.index] = {
              ...updated[editingRow.index],
              autoNotes: main.autoNotes ?? [],
              ingredientNotes: main.ingredientNotes ?? {},
              selectionState: main.selectionState ?? {},
              combinations: main.combinations ?? updated[editingRow.index].combinations,
              productId: main.productId ?? updated[editingRow.index].productId,

              // ✅ ADD THIS:
              priceOption: main.priceOption ?? updated[editingRow.index].priceOption,
            };

            // VARIANT PRODUCTS
            if (results.length > 1) {

              // STEP 1 — REMOVE OLD VARIANT ROWS
              // remove all rows directly after main row until hitting next main product or end
              let removeCount = 0;
              for (let i = editingRow.index + 1; i < updated.length; i++) {
                const row = updated[i];

                // a "main" row has the same label as productName
                if (row.label === productName) break;

                removeCount++;
              }

              if (removeCount > 0) {
                updated.splice(editingRow.index + 1, removeCount);    // remove old rows
              }

              // STEP 2 — ADD NEW VARIANT ROWS
              const extraRows = results.slice(1).map((r, childIdx) => {
                const parentPrice = updated[editingRow.index]?.priceOption || {};
                const rawChildPrice = r.priceOption || {};

                const childPrice = {
                  ...parentPrice,
                  ...rawChildPrice,
                };

                const fkPriceCodeId =
                  childPrice.priceCodeId ??
                  childPrice.PriceCodeID ??
                  parentPrice.priceCodeId ??
                  parentPrice.PriceCodeID ??
                  null;

                const priceCode =
                  childPrice.priceCode ??
                  childPrice.PriceCode ??
                  parentPrice.priceCode ??
                  parentPrice.PriceCode ??
                  null;

                const vat =
                  childPrice.vat ??
                  childPrice.Vat ??
                  parentPrice.vat ??
                  parentPrice.Vat ??
                  0;

                const unitPrice = Number(
                  childPrice.UnitCostIncl?.parsedValue ??
                  childPrice.ItemPrice ??
                  childPrice.UnitPrice ??
                  childPrice.unitPrice ??
                  childPrice.Price ??
                  childPrice.price ??
                  0
                );

                const unit =
                  childPrice.unit ??
                  childPrice.Unit ??
                  parentPrice.unit ??
                  parentPrice.Unit ??
                  null;

                const priceDesc =
                  childPrice.Description ??
                  childPrice.description ??
                  parentPrice.Description ??
                  parentPrice.description ??
                  "";

                return {
                  // unique line id
                  TabLineID: r.TabLineID ?? `TEMP-${Date.now()}-${editingRow.index}-${childIdx}`,

                  // core product identity
                  FK_ProductID: r.productId ?? updated[editingRow.index].FK_ProductID,
                  Product: r.label ?? updated[editingRow.index].Product,

                  // ✅ label used by the options modal header
                  label:
                    r.label ??
                    updated[editingRow.index].label ??
                    updated[editingRow.index].Product,

                  // pricing
                  FK_PriceCodeID: fkPriceCodeId,
                  PriceCode: priceCode,
                  UnitPrice: unitPrice,
                  Vat: vat,
                  Unit: unit,
                  PriceDescription: priceDesc,

                  // child flags
                  _isChild: true,
                  PointerID: r.PointerID ?? null,
                  SubstituteName: r.SubstituteName ?? r.label ?? null,

                  Quantity: 1,

                  // guest info
                  guest: updated[editingRow.index].guest ?? null,
                  guestId: updated[editingRow.index].guestId ?? null,
                  accountGuestId: updated[editingRow.index].accountGuestId ?? null,

                  // notes & state
                  autoNotes: r.autoNotes ?? [],
                  ingredientNotes: r.ingredientNotes ?? {},
                  selectionState: r.selectionState ?? {},
                  combinations: r.combinations ?? [],
                  productId: r.productId ?? updated[editingRow.index].productId,

                  // raw price
                  priceOption: childPrice,
                  PriceOptionRaw: childPrice,

                  UnitCostIncl: {
                    parsedValue: unitPrice,
                  },
                };
              });

              updated.splice(editingRow.index + 1, 0, ...extraRows);
            }

            setRows(updated);
            setEditingRow(null);
          }}
        />
      )}
    </>
  );
}

AddProductOptionsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  productName: PropTypes.string,
  selectedItems: PropTypes.arrayOf(PropTypes.object),
  productId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  editIndex: PropTypes.number,
};

AddProductOptionsModal.defaultProps = {
  productName: "",
  selectedItems: [],
  productId: null,
  editIndex: null,
};
